/**
 * Reportes → Movimientos en cuenta (`/reportes/movimientos-cuenta`).
 *
 * Puerto del listado de *Movimientos* de la cuenta (`Bank_accounts::movements` +
 * `Bank_accounts_model::get_movements` y `get_cancelled_movements` +
 * `views/bank_accounts/movements.php`), que es la ficha «Reporte de Movimientos en Cuenta»
 * del centro de reportes.
 *
 * La pantalla del panel anterior pedía **fecha de inicio, fecha de fin, banco y cuenta** y
 * mostraba dos tablas: los movimientos **vigentes** (fecha de registro, fecha original,
 * cliente/proveedor, documento, referencia, banco, cuenta, tipo, subtipo, monto,
 * dispersión y archivo) y los **anulados** (con quién los anuló).
 *
 * Reglas conservadas:
 * - Los movimientos salen de `movements_account` con el banco y la cuenta
 *   (`bank_accounts` → `banks`), el tipo (`abc_tipo_movimiento`, que además marca si es
 *   **INGRESO** o **EGRESO**) y el método de pago (`abc_metodo_pago`); el rango va por
 *   `movement_date`.
 * - Los **vigentes** son los que no están `ANULADO` (registrados y pendientes) y los
 *   **anulados** van en su propio bloque, con el ejecutor y quién anuló.
 *
 * Diferencias anotadas:
 * - Es una pantalla de **consulta**: las acciones del panel anterior (anular un
 *   movimiento, subir o reemplazar el archivo de dispersión) pertenecen al módulo de
 *   bancos y no se replican aquí; el archivo de sustento se muestra como referencia.
 * - Se añaden las tarjetas del periodo, el reparto por **cuenta** y por **tipo**, el
 *   resumen legible de los pagos aplicados (`movement_detail`) y el CSV/PDF servidos por
 *   la API.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatDateTimeValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosMovimientosCuenta,
  useMovimientosCuenta,
  type FiltrosMovimientosCuenta,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto: el año en curso. */
function rangoPorDefecto(): FiltrosMovimientosCuenta {
  const hoy = new Date();

  return {
    desde: `${hoy.getFullYear()}-01-01`,
    hasta: fechaLocal(hoy),
    banco: '',
    cuenta: '',
    estado: 'vigentes',
  };
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

export function ReporteMovimientosCuentaPage() {
  useDocumentTitle('Reportes · Movimientos en cuenta');

  const [filtros, setFiltros] = useState<FiltrosMovimientosCuenta>(rangoPorDefecto);
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosMovimientosCuenta();
  const consulta = useMovimientosCuenta(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const filas = useMemo(() => datos?.filas ?? [], [datos]);
  // El listado se pagina en el navegador; los contadores y el pie siguen usando el
  // listado completo.
  const paginacion = usePaginacion(filas, { clave: filtros });
  const porCuenta = datos?.por_cuenta ?? [];
  const porTipo = datos?.por_tipo ?? [];

  const bancos = catalogos.data?.bancos ?? [];
  const cuentas = catalogos.data?.cuentas ?? [];
  const estados = catalogos.data?.estados ?? [];
  const meses = catalogos.data?.meses ?? [];

  const cuentasDelBanco = useMemo(
    () => (filtros.banco === '' ? cuentas : cuentas.filter((c) => String(c.banco_id) === filtros.banco)),
    [cuentas, filtros.banco],
  );

  const cambiar = <C extends keyof FiltrosMovimientosCuenta>(
    campo: C,
    valor: FiltrosMovimientosCuenta[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarMovimientosCuenta(filtros), {
        nombre: `movimientos-cuenta-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfMovimientosCuenta(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const rangoDe = (mes: string) => {
    const anio = Number(mes.slice(0, 4));
    const numero = Number(mes.slice(5, 7));

    return { desde: `${mes}-01`, hasta: fechaLocal(new Date(anio, numero, 0)) };
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Movimientos en cuenta</h1>
          <p className="mt-1 text-sm text-ink-500">
            Los movimientos bancarios del periodo: ingresos, egresos, su archivo de sustento y los
            anulados.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Banco</span>
              <Select
                value={filtros.banco}
                onChange={({ target }) => setFiltros((previos) => ({ ...previos, banco: target.value, cuenta: '' }))}
                className={CONTROL}
                aria-label="Banco"
              >
                <option value="">TODOS</option>
                {bancos.map((banco) => (
                  <option key={banco.id} value={String(banco.id)}>
                    {banco.banco} ({banco.cuentas})
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Cuenta</span>
              <Select
                value={filtros.cuenta}
                onChange={({ target }) => cambiar('cuenta', target.value)}
                className={CONTROL}
                aria-label="Cuenta"
              >
                <option value="">TODAS</option>
                {cuentasDelBanco.map((cuenta) => (
                  <option key={cuenta.id} value={String(cuenta.id)}>
                    {cuenta.banco} · {cuenta.cuenta} ({cuenta.movimientos})
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t border-ink-100 pt-3">
            <div className="inline-flex overflow-hidden rounded-control border border-ink-200">
              {estados.map((estado) => (
                <button
                  key={estado.valor}
                  type="button"
                  aria-pressed={filtros.estado === estado.valor}
                  onClick={() => cambiar('estado', estado.valor as FiltrosMovimientosCuenta['estado'])}
                  className={cn(
                    'px-3 py-2 text-xs font-semibold transition-colors',
                    filtros.estado === estado.valor
                      ? 'bg-brand-50 text-brand-700'
                      : 'bg-white text-ink-600 hover:text-brand-700',
                  )}
                >
                  {estado.valor === 'anulados' ? 'Anulados' : 'Vigentes'}
                </button>
              ))}
            </div>

            {meses.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                  <Search className="size-3.5" aria-hidden="true" />
                  Meses con movimientos
                </span>
                {meses.slice(0, 12).map((mes) => {
                  const rango = rangoDe(mes.periodo);
                  const activo = rango.desde === filtros.desde && rango.hasta === filtros.hasta;

                  return (
                    <button
                      key={mes.periodo}
                      type="button"
                      aria-pressed={activo}
                      onClick={() => setFiltros((previos) => ({ ...previos, ...rango }))}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                        activo
                          ? 'border-brand-300 bg-brand-50 text-brand-700'
                          : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                      )}
                      title={`${formatNumber(mes.movimientos)} movimiento(s)`}
                    >
                      {etiquetaMes(mes.periodo)}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {[
          {
            etiqueta: 'Movimientos',
            valor: formatNumber(resumen?.movimientos ?? 0),
            detalle: `${formatNumber(resumen?.con_archivo ?? 0)} con archivo`,
          },
          {
            etiqueta: 'Ingresos',
            valor: formatCurrency(resumen?.ingresos ?? 0),
            detalle: 'del periodo',
          },
          {
            etiqueta: 'Egresos',
            valor: formatCurrency(resumen?.egresos ?? 0),
            detalle: 'del periodo',
          },
          {
            etiqueta: 'Saldo del periodo',
            valor: formatCurrency(resumen?.monto ?? 0),
            detalle: 'ingresos menos egresos',
          },
          {
            etiqueta: 'Estado',
            valor: `${formatNumber(resumen?.registrados ?? 0)} / ${formatNumber(resumen?.pendientes ?? 0)}`,
            detalle: 'registrados / pendientes',
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="min-w-0">
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por cuenta</h2>

            <div className="overflow-x-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Cuenta</th>
                    <th className="w-[4.5rem] px-2 py-2 text-right">Movim.</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right">Ingresos</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right">Egresos</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {porCuenta.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={5} className="px-2 py-8 text-center text-sm text-ink-500">
                        No hay movimientos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {porCuenta.map((fila) => (
                    <tr key={fila.cuenta} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="px-2 py-2">
                        <p className="truncate font-semibold text-ink-900" title={fila.cuenta ?? ''}>
                          {fila.cuenta ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">
                          {fila.banco} · {fila.numero_cuenta ?? ''}
                        </p>
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.movimientos)}
                      </td>
                      <td className="px-2 py-2 text-right text-emerald-700">
                        {formatCurrency(fila.ingresos)}
                      </td>
                      <td className="px-2 py-2 text-right text-rose-700">
                        {formatCurrency(fila.egresos)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.saldo)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por tipo de movimiento</h2>

            <div className="overflow-x-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Tipo</th>
                    <th className="w-[5rem] px-2 py-2 text-right">Movim.</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {porTipo.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={3} className="px-2 py-8 text-center text-sm text-ink-500">
                        No hay movimientos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {porTipo.map((fila) => (
                    <tr key={fila.tipo} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="px-2 py-2 font-semibold text-ink-900">{fila.tipo ?? '—'}</td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.movimientos)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.monto)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Movimientos ({formatNumber(resumen?.movimientos ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {formatDateValue(filtros.desde)} — {formatDateValue(filtros.hasta)} ·{' '}
              {filtros.estado === 'anulados' ? 'anulados' : 'vigentes'}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[3.5rem] px-1.5 py-2">ID</th>
                    <th className="w-[5.25rem] px-1.5 py-2">Fecha</th>
                    <th className="px-1.5 py-2">Cuenta</th>
                    <th className="w-[7.5rem] px-1.5 py-2">Tipo</th>
                    <th className="w-[7rem] px-1.5 py-2">Método</th>
                    <th className="w-[5.5rem] px-1.5 py-2 text-right">Monto</th>
                    <th className="w-[7rem] px-1.5 py-2">Estado</th>
                    <th className="px-1.5 py-2">Referencia y archivo</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={8} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay movimientos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila) => (
                    <tr key={fila.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="px-1.5 py-2 text-ink-600">{fila.id}</td>
                      <td className="px-1.5 py-2 text-ink-700">{formatDateValue(fila.fecha)}</td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900" title={fila.cuenta ?? ''}>
                          {fila.cuenta ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">
                          {fila.banco} · {fila.numero_cuenta ?? ''}
                        </p>
                        {fila.cliente ? (
                          <p className="truncate text-[11px] text-ink-400" title={fila.cliente}>
                            {fila.cliente} · {fila.documento ?? ''}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.tipo ?? ''}>
                          {fila.tipo ?? '—'}
                        </p>
                        {fila.direccion ? (
                          <Badge
                            variant={fila.direccion === 'INGRESO' ? 'success' : 'danger'}
                            className="mt-0.5 px-1.5 py-0 text-[10px]"
                          >
                            {fila.direccion}
                          </Badge>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-700" title={fila.metodo ?? ''}>
                          {fila.metodo ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">
                          {formatDateTimeValue(fila.registrado_at)}
                        </p>
                      </td>
                      <td className="px-1.5 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.monto)}
                      </td>
                      <td className="px-1.5 py-2">
                        <Badge
                          variant={
                            fila.estado === 'ANULADO'
                              ? 'danger'
                              : fila.estado === 'REGISTRADO'
                                ? 'success'
                                : 'warning'
                          }
                          className="px-1.5 py-0 text-[10px]"
                        >
                          {fila.estado}
                        </Badge>
                        {fila.anulado_por ? (
                          <p className="truncate text-[11px] text-ink-500" title={fila.anulado_por}>
                            {fila.anulado_por}
                          </p>
                        ) : fila.ejecutor ? (
                          <p className="truncate text-[11px] text-ink-400" title={fila.ejecutor}>
                            {fila.ejecutor}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-600" title={fila.detalle ?? fila.notas ?? ''}>
                          {fila.detalle ?? fila.notas ?? fila.referencia ?? '—'}
                        </p>
                        {fila.archivo ? (
                          <p className="truncate text-[11px] text-ink-400" title={fila.archivo}>
                            archivo: {fila.archivo}
                          </p>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {filas.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={5}>
                        TOTAL · {formatNumber(resumen?.movimientos ?? 0)} movimiento(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.monto ?? 0)}
                      </td>
                      <td className="px-1.5 py-2" colSpan={2}>
                        <span className="text-[11px] font-normal text-ink-500">
                          ingresos {formatCurrency(resumen?.ingresos ?? 0)} · egresos{' '}
                          {formatCurrency(resumen?.egresos ?? 0)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="movimientos" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Los <strong>vigentes</strong> son los movimientos registrados y pendientes (todo lo que no
          está anulado) y los <strong>anulados</strong> van en su propio bloque, con quién los anuló.
          El sentido del importe (ingreso o egreso) lo define el tipo de movimiento, y el archivo es
          el sustento cargado en el panel anterior; las acciones para anular o cargar el archivo
          viven en el módulo de bancos.
        </span>
      </p>
    </div>
  );
}
